import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import API_BASE_URL from "../config";

const API_BASE = `${API_BASE_URL}api/RackingsInventory`;
const INVENTORY_API = `${API_BASE_URL}api/InventoryData`;

const STATUS_CONFIG = {
  Active:   { bg: "#eff6ff", text: "#1d4ed8", dot: "#3b82f6" },
  Inactive: { bg: "#f1f5f9", text: "#64748b", dot: "#94a3b8" },
  Reserved: { bg: "#e0f2fe", text: "#0369a1", dot: "#38bdf8" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG["Inactive"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: cfg.bg, color: cfg.text,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
      padding: "2px 8px", borderRadius: 20,
      border: `1px solid ${cfg.dot}30`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

function StockBadge({ stockInfo, loading }) {
  if (loading) return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "#f1f5f9", color: "#94a3b8",
      fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
    }}>
      <span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #cbd5e1", borderTopColor: "#3b82f6", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
      ...
    </span>
  );

  if (!stockInfo) return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "#f8fafc", color: "#94a3b8",
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
      border: "1px solid #e2e8f0",
    }}>
      No stock
    </span>
  );

  const qty = stockInfo.quantity ?? stockInfo.availableQty ?? 0;
  const inStock = qty > 0;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: inStock ? "#f0fdf4" : "#f8fafc",
      color: inStock ? "#15803d" : "#64748b",
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
      border: `1px solid ${inStock ? "#86efac" : "#e2e8f0"}`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: inStock ? "#22c55e" : "#cbd5e1", flexShrink: 0 }} />
      {inStock ? `${qty.toLocaleString()} ${stockInfo.uom || ""}`.trim() : "No stock"}
    </span>
  );
}

function RackCard({ rackNo, items, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState({});

  useEffect(() => {
    const codes = [...new Set(items.map(i => i.itemCode))];
    codes.forEach(async (code) => {
      setStockLoading(prev => ({ ...prev, [code]: true }));
      try {
        const res = await fetch(`${INVENTORY_API}/item/${encodeURIComponent(code)}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        const entry = Array.isArray(data) ? data[0] : data;
        setStockMap(prev => ({ ...prev, [code]: entry || null }));
      } catch {
        setStockMap(prev => ({ ...prev, [code]: null }));
      } finally {
        setStockLoading(prev => ({ ...prev, [code]: false }));
      }
    });
  }, [items]);

  const filtered = items.filter(i =>
    i.itemCode.toLowerCase().includes(search.toLowerCase()) ||
    i.itemDescription.toLowerCase().includes(search.toLowerCase())
  );
  const preview = expanded ? filtered : filtered.slice(0, 10);
  const activeCount = items.filter(i => i.status === "Active").length;

  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        padding: "14px 16px 12px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 9,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M2 10h20M7 3v7M12 3v7M17 3v7" />
              </svg>
            </div>
            <div>
              <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 700 }}>{rackNo}</div>
              <div style={{ color: "#64748b", fontSize: 11 }}>{items.length} items · {activeCount} active</div>
            </div>
          </div>
          <button onClick={() => setExpanded(e => !e)} style={{
            background: "#eff6ff", border: "1px solid #bfdbfe",
            color: "#2563eb", borderRadius: 7, padding: "4px 10px",
            fontSize: 11, cursor: "pointer", fontWeight: 600,
          }}>
            {expanded ? "↑ Collapse" : "↓ Expand"}
          </button>
        </div>

        <div style={{ position: "relative" }}>
          <svg style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)" }}
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search items..."
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#fff", border: "1px solid #e2e8f0",
              borderRadius: 7, padding: "6px 10px 6px 28px",
              color: "#0f172a", fontSize: 12, outline: "none",
              fontFamily: "inherit",
            }}
          />
        </div>
      </div>

      {/* Items */}
      <div style={{ flex: 1, padding: "10px 12px 12px", background: "#fff" }}>
        {preview.length === 0 ? (
          <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: "18px 0" }}>
            No items found
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {preview.map((item) => (
              <div key={item.id} style={{
                display: "flex", flexDirection: "column", gap: 5,
                padding: "9px 10px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 9,
              }}>
                {/* Item code + status row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, flexWrap: "wrap" }}>
                  <span style={{
                    background: "#eff6ff", color: "#1d4ed8",
                    fontSize: 10, fontWeight: 700, padding: "2px 6px",
                    borderRadius: 4, border: "1px solid #bfdbfe",
                    letterSpacing: "0.03em", flexShrink: 0,
                  }}>{item.itemCode}</span>
                  <StatusBadge status={item.status} />
                </div>

                {/* Description */}
                <div style={{
                  color: "#374151", fontSize: 11, lineHeight: 1.4,
                  wordBreak: "break-word", flex: 1,
                }}>{item.itemDescription}</div>

                {/* Stock badge */}
                <div>
                  <StockBadge
                    stockInfo={stockMap[item.itemCode]}
                    loading={stockLoading[item.itemCode] && !(item.itemCode in stockMap)}
                  />
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                  <button onClick={() => onEdit(item)} style={{
                    background: "#fff", border: "1px solid #e2e8f0",
                    color: "#64748b", borderRadius: 6, padding: "3px 8px",
                    fontSize: 11, cursor: "pointer",
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button onClick={() => onDelete(item.id)} style={{
                    background: "#fff5f5", border: "1px solid #fecaca",
                    color: "#dc2626", borderRadius: 6, padding: "3px 8px",
                    fontSize: 11, cursor: "pointer",
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!expanded && filtered.length > 10 && (
          <button onClick={() => setExpanded(true)} style={{
            width: "100%", background: "#f8fafc",
            border: "1px dashed #bfdbfe", color: "#2563eb",
            borderRadius: 8, padding: "7px",
            fontSize: 12, cursor: "pointer", marginTop: 8, fontWeight: 600,
          }}>
            +{filtered.length - 10} more items
          </button>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#fff", border: "1px solid #e2e8f0",
        borderRadius: 16, padding: "26px 28px", width: "100%", maxWidth: 480,
        maxHeight: "85vh", overflowY: "auto",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ color: "#0f172a", fontSize: 15, fontWeight: 700, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{
            background: "#f1f5f9", border: "none", color: "#64748b",
            fontSize: 15, cursor: "pointer", width: 28, height: 28,
            borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
          }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", color: "#374151", fontSize: 12, fontWeight: 600, marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: "#f8fafc", border: "1px solid #e2e8f0",
  borderRadius: 8, padding: "8px 12px",
  color: "#0f172a", fontSize: 13,
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  outline: "none",
};

const btnPrimary = {
  background: "#2563eb", color: "#fff", border: "none",
  borderRadius: 8, padding: "9px 20px", fontWeight: 600,
  fontSize: 13, cursor: "pointer",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
};

const btnSecondary = {
  background: "#fff", color: "#374151",
  border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 20px",
  fontSize: 13, cursor: "pointer", fontWeight: 500,
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
};

const EMPTY_FORM = { itemCode: "", itemDescription: "", status: "Active", rackNo: "" };

export default function Rackings_Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importRack, setImportRack] = useState("");
  const [importStatus, setImportStatus] = useState("Active");
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef();

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_BASE);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const rackGroups = items.reduce((acc, item) => {
    const key = item.rackNo || "Unassigned";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  const racks = Object.keys(rackGroups).sort();

  const openAdd = () => { setForm(EMPTY_FORM); setEditItem(null); setShowForm(true); };
  const openEdit = (item) => {
    setForm({ itemCode: item.itemCode, itemDescription: item.itemDescription, status: item.status, rackNo: item.rackNo });
    setEditItem(item);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.itemCode || !form.itemDescription || !form.rackNo) {
      showToast("Please fill all required fields", "error"); return;
    }
    setSaving(true);
    try {
      let res;
      if (editItem) {
        res = await fetch(`${API_BASE}/${editItem.id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...editItem, ...form }),
        });
      } else {
        res = await fetch(API_BASE, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      }
      if (!res.ok) throw new Error("Save failed");
      showToast(editItem ? "Item updated!" : "Item added!");
      setShowForm(false);
      fetchItems();
    } catch (e) { showToast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setItems(prev => prev.filter(i => i.id !== id));
      showToast("Item deleted");
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      showToast("Please upload a valid .xlsx, .xls, or .csv file", "error"); return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result, { type: "binary" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      if (rows.length < 2) return;
      const header = rows[0].map(h => String(h).toLowerCase().trim());
      const codeIdx = header.findIndex(h => h === "itemcode" || h.includes("code"));
      const descIdx = header.findIndex(h => h === "itemdescription" || h.includes("description") || h.includes("desc"));
      const statusIdx = header.findIndex(h => h === "status");
      const finalCodeIdx = codeIdx >= 0 ? codeIdx : 0;
      const finalDescIdx = descIdx >= 0 ? descIdx : 1;
      const parsed = rows.slice(1)
        .filter(r => r[finalCodeIdx] || r[finalDescIdx])
        .map(r => ({
          itemCode: String(r[finalCodeIdx] || "").trim(),
          itemDescription: String(r[finalDescIdx] || "").trim(),
          ...(statusIdx >= 0 && r[statusIdx] ? { _statusFromExcel: String(r[statusIdx]).trim() } : {}),
        }));
      setImportData(parsed);
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (!importRack) { showToast("Please enter a Rack No", "error"); return; }
    if (importData.length === 0) { showToast("No data to import", "error"); return; }
    setImporting(true);
    let success = 0, fail = 0;
    for (const row of importData) {
      try {
        const res = await fetch(API_BASE, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...row, status: row._statusFromExcel || importStatus, rackNo: importRack }),
        });
        if (res.ok) success++; else fail++;
      } catch { fail++; }
    }
    setImporting(false);
    showToast(`Imported ${success} items${fail ? `, ${fail} failed` : ""}`, fail ? "error" : "success");
    setShowImport(false); setImportData([]); setImportRack(""); fetchItems();
  };

  const totalActive = items.filter(i => i.status === "Active").length;
  const totalInactive = items.filter(i => i.status === "Inactive").length;

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f8fafc",
      padding: "28px 24px",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      color: "#0f172a",
    }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 18, right: 18, zIndex: 2000,
          background: toast.type === "error" ? "#fff5f5" : "#f0fdf4",
          border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
          color: toast.type === "error" ? "#dc2626" : "#15803d",
          padding: "11px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
        }}>
          {toast.type === "error" ? "⚠ " : "✓ "}{toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11,
              background: "#eff6ff", border: "1px solid #bfdbfe",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4M2 10h20M7 3v7M12 3v7M17 3v7" />
              </svg>
            </div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: "#0f172a", letterSpacing: "-0.02em" }}>
                Rack Inventory
              </h1>
              <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>
                {items.length} items · {racks.length} racks
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setShowImport(true)} style={{
              ...btnSecondary, display: "flex", alignItems: "center", gap: 6,
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Import Excel
            </button>
            <button onClick={openAdd} style={{
              ...btnPrimary, display: "flex", alignItems: "center", gap: 6,
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Item
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          {[
            { label: "Total Items", value: items.length, bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af" },
            { label: "Racks", value: racks.length, bg: "#f0f9ff", border: "#bae6fd", text: "#0369a1" },
            { label: "Active", value: totalActive, bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
            { label: "Inactive", value: totalInactive, bg: "#f8fafc", border: "#e2e8f0", text: "#475569" },
          ].map(s => (
            <div key={s.label} style={{
              background: s.bg, border: `1px solid ${s.border}`,
              borderRadius: 11, padding: "11px 18px", flex: "1 1 110px",
            }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>
                {s.label}
              </div>
              <div style={{ color: s.text, fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: "#e2e8f0", marginBottom: 20 }} />

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: "center", color: "#2563eb", padding: "60px 0", fontSize: 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid #bfdbfe", borderTopColor: "#2563eb", animation: "spin 0.7s linear infinite", margin: "0 auto 12px" }} />
          Loading racks...
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : error ? (
        <div style={{
          background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 12,
          padding: "22px", color: "#dc2626", textAlign: "center",
        }}>
          ⚠ {error} — Make sure the backend is running at {API_BASE}
        </div>
      ) : racks.length === 0 ? (
        <div style={{ textAlign: "center", color: "#cbd5e1", padding: "60px 0" }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: "0 auto 12px", display: "block" }}>
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M2 10h20" />
          </svg>
          <div style={{ fontSize: 14, color: "#94a3b8" }}>No racks yet. Add an item or import from Excel.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
          {racks.map((rackNo) => (
            <RackCard key={rackNo} rackNo={rackNo} items={rackGroups[rackNo]}
              onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <Modal title={editItem ? "Edit Item" : "Add New Item"} onClose={() => setShowForm(false)}>
          <FieldGroup label="Item Code *">
            <input style={inputStyle} value={form.itemCode}
              onChange={e => setForm(f => ({ ...f, itemCode: e.target.value }))}
              placeholder="e.g. MES0041" />
          </FieldGroup>
          <FieldGroup label="Item Description *">
            <input style={inputStyle} value={form.itemDescription}
              onChange={e => setForm(f => ({ ...f, itemDescription: e.target.value }))}
              placeholder="e.g. ALLEN CAP SCREW 10MM X 20" />
          </FieldGroup>
          <FieldGroup label="Rack No *">
            <input style={inputStyle} value={form.rackNo}
              onChange={e => setForm(f => ({ ...f, rackNo: e.target.value }))}
              placeholder="e.g. RACK 1" list="rack-list" />
            <datalist id="rack-list">
              {racks.map(r => <option key={r} value={r} />)}
            </datalist>
          </FieldGroup>
          <FieldGroup label="Status">
            <select style={inputStyle} value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option>Active</option>
              <option>Inactive</option>
              <option>Reserved</option>
            </select>
          </FieldGroup>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 22 }}>
            <button onClick={() => setShowForm(false)} style={btnSecondary}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving..." : editItem ? "Update" : "Add Item"}
            </button>
          </div>
        </Modal>
      )}

      {/* Import Modal */}
      {showImport && (
        <Modal title="Import from Excel / CSV" onClose={() => setShowImport(false)}>
          <FieldGroup label="Rack No *">
            <input style={inputStyle} value={importRack}
              onChange={e => setImportRack(e.target.value)}
              placeholder="e.g. RACK 1" list="rack-list2" />
            <datalist id="rack-list2">
              {racks.map(r => <option key={r} value={r} />)}
            </datalist>
          </FieldGroup>
          <FieldGroup label="Default Status">
            <select style={inputStyle} value={importStatus} onChange={e => setImportStatus(e.target.value)}>
              <option>Active</option>
              <option>Inactive</option>
              <option>Reserved</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Excel / CSV File">
            <div onClick={() => fileRef.current.click()} style={{
              border: `2px dashed ${importData.length ? "#86efac" : "#e2e8f0"}`,
              borderRadius: 10, padding: "20px", textAlign: "center", cursor: "pointer",
              color: importData.length ? "#15803d" : "#94a3b8", fontSize: 13, fontWeight: 500,
              background: importData.length ? "#f0fdf4" : "#f8fafc",
            }}>
              {importData.length > 0
                ? `✓ ${importData.length} rows ready to import`
                : "Click to select .xlsx or .csv file"}
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
              style={{ display: "none" }} onChange={handleFileChange} />
          </FieldGroup>

          {importData.length > 0 && (
            <div style={{
              background: "#f8fafc", border: "1px solid #e2e8f0",
              borderRadius: 9, overflow: "auto", maxHeight: 180, marginBottom: 14,
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ padding: "7px 12px", color: "#374151", textAlign: "left", fontWeight: 700 }}>Item Code</th>
                    <th style={{ padding: "7px 12px", color: "#374151", textAlign: "left", fontWeight: 700 }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {importData.slice(0, 20).map((r, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "5px 12px", color: "#2563eb", fontWeight: 600 }}>{r.itemCode}</td>
                      <td style={{ padding: "5px 12px", color: "#374151" }}>{r.itemDescription}</td>
                    </tr>
                  ))}
                  {importData.length > 20 && (
                    <tr><td colSpan={2} style={{ padding: "5px 12px", color: "#94a3b8", textAlign: "center" }}>
                      +{importData.length - 20} more rows...
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setShowImport(false)} style={btnSecondary}>Cancel</button>
            <button onClick={handleImport} disabled={importing || importData.length === 0}
              style={{ ...btnPrimary, opacity: (importing || importData.length === 0) ? 0.5 : 1 }}>
              {importing ? "Importing..." : `Import ${importData.length} Items`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}